import { styled } from '@mui/material/styles';
import ButtonBase from '@mui/material/ButtonBase';
import Button from '@mui/material/Button';
import { MINT_MIST } from '../../constants/brandSurfaces';

const blue = '#0d4efe';
const teal = '#01ebb2';
const ink = '#1a1a2e';
const muted = '#5a6478';
const headingFont = "'Plus Jakarta Sans', sans-serif";

/** Trade color set as `--trade` on each TradeCard. */
const trade = 'var(--trade, #0d4efe)';

/* ─── Intro + trade picker ─── */
export const IntroPanel = styled('section')({
  marginTop: 12,
  padding: '48px 52px',
  borderRadius: 24,
  background: MINT_MIST,
  border: '1px solid rgba(26, 26, 46, 0.06)',
  textAlign: 'left',
  '@media (max-width: 900px)': {
    padding: '36px 28px',
  },
  '@media (max-width: 600px)': {
    borderRadius: 18,
    padding: '26px 16px 22px',
  },
});

export const Eyebrow = styled('p')({
  margin: '0 0 8px',
  fontSize: '0.95rem',
  fontWeight: 700,
  color: blue,
});

export const IntroHeading = styled('h1')({
  fontFamily: headingFont,
  fontSize: '2.6rem',
  fontWeight: 700,
  lineHeight: 1.15,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 12px',
  '@media (max-width: 900px)': {
    fontSize: '2.2rem',
  },
  '@media (max-width: 600px)': {
    fontSize: '1.75rem',
  },
});

export const IntroLead = styled('p')({
  fontSize: '1.1rem',
  lineHeight: 1.55,
  color: muted,
  margin: '0 0 28px',
  maxWidth: 620,
  '@media (max-width: 600px)': {
    fontSize: '1rem',
    marginBottom: 20,
  },
});

export const TradeGrid = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: 12,
  '@media (max-width: 900px)': {
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  },
  '@media (max-width: 600px)': {
    gap: 8,
  },
});

export const TradeCard = styled(ButtonBase)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '10px 12px',
  borderRadius: 14,
  textAlign: 'left',
  fontFamily: 'inherit',
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.07)',
  boxShadow: '0 2px 8px rgba(26, 26, 46, 0.04)',
  transition: 'border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease',
  '&:hover, &.Mui-focusVisible': {
    borderColor: `color-mix(in srgb, ${trade} 45%, transparent)`,
    boxShadow: `0 8px 18px color-mix(in srgb, ${trade} 14%, transparent)`,
    transform: 'translateY(-1px)',
  },
});

export const TradeBody = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  width: '100%',
  minWidth: 0,
});

export const TradeIcon = styled('span')({
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  height: 32,
  borderRadius: 10,
  color: trade,
  background: `color-mix(in srgb, ${trade} 12%, #ffffff)`,
  '& svg': { fontSize: 18 },
});

export const TradeName = styled('span')({
  display: 'block',
  fontSize: '0.92rem',
  fontWeight: 700,
  lineHeight: 1.25,
  color: ink,
});

export const TradeHint = styled('span')({
  display: 'block',
  marginTop: 1,
  fontSize: '0.78rem',
  lineHeight: 1.35,
  color: muted,
});

export const OtherLink = styled(ButtonBase)({
  marginTop: 18,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '9px 16px 9px 12px',
  borderRadius: 999,
  fontFamily: 'inherit',
  fontSize: '0.95rem',
  fontWeight: 600,
  color: ink,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.08)',
  transition: 'border-color 0.15s ease, color 0.15s ease',
  '& svg': { fontSize: 18 },
  '& svg:first-of-type': { color: '#475569' },
  '& svg:last-of-type': { color: blue },
  '&:hover, &.Mui-focusVisible': {
    borderColor: 'rgba(13, 78, 254, 0.35)',
    color: blue,
  },
});

/* ─── How it works ─── */
export const Section = styled('section')({
  marginTop: 40,
  textAlign: 'left',
  '@media (max-width: 600px)': {
    marginTop: 28,
  },
});

export const SectionTitle = styled('h2')({
  fontFamily: headingFont,
  fontSize: '1.75rem',
  fontWeight: 700,
  lineHeight: 1.2,
  letterSpacing: '-0.02em',
  color: ink,
  margin: '0 0 18px',
  '@media (max-width: 600px)': {
    fontSize: '1.4rem',
  },
});

export const StepList = styled('ol')({
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 14,
  '@media (max-width: 900px)': {
    gridTemplateColumns: '1fr',
    gap: 10,
  },
});

export const Step = styled('li')({
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  padding: '18px',
  borderRadius: 16,
  background: '#ffffff',
  border: '1px solid rgba(26, 26, 46, 0.06)',
  boxShadow: '0 4px 16px rgba(26, 26, 46, 0.05)',
});

export const StepIcon = styled('span')({
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 44,
  height: 44,
  borderRadius: '50%',
  color: blue,
  background: 'rgba(1, 235, 178, 0.18)',
  '& svg': { fontSize: 22 },
});

export const StepTitle = styled('h3')({
  fontSize: '1.02rem',
  fontWeight: 700,
  color: ink,
  margin: '0 0 2px',
});

export const StepText = styled('p')({
  fontSize: '0.94rem',
  lineHeight: 1.5,
  color: muted,
  margin: 0,
});

/* ─── Smart Search card ─── */
export const SmartCard = styled('section')({
  margin: '32px 0 48px',
  display: 'flex',
  alignItems: 'center',
  gap: 18,
  padding: '22px 26px',
  borderRadius: 20,
  background: MINT_MIST,
  border: '1px solid rgba(26, 26, 46, 0.06)',
  textAlign: 'left',
  '@media (max-width: 700px)': {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 12,
    padding: '20px 18px',
    margin: '24px 0 32px',
  },
});

export const SmartIcon = styled('span')({
  flex: '0 0 auto',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 48,
  height: 48,
  borderRadius: '50%',
  color: blue,
  background: 'rgba(1, 235, 178, 0.18)',
  '& svg': { fontSize: 24 },
});

export const SmartText = styled('div')({
  flex: 1,
  minWidth: 0,
});

export const SmartTitle = styled('h2')({
  fontFamily: headingFont,
  fontSize: '1.15rem',
  fontWeight: 700,
  color: ink,
  margin: '0 0 4px',
});

export const SmartBody = styled('p')({
  fontSize: '0.96rem',
  lineHeight: 1.5,
  color: muted,
  margin: 0,
});

export const SmartButton = styled(Button)({
  flex: '0 0 auto',
  textTransform: 'none',
  borderRadius: 10,
  padding: '10px 24px',
  fontSize: '0.98rem',
  fontWeight: 700,
  color: '#ffffff',
  background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
  boxShadow: '0 4px 14px rgba(13, 78, 254, 0.35)',
  '&:hover': {
    color: '#ffffff',
    background: `linear-gradient(90deg, ${teal} 0%, ${blue} 100%)`,
    filter: 'brightness(1.05)',
    boxShadow: '0 6px 18px rgba(13, 78, 254, 0.4)',
  },
});
